import { AnimatedNumberReactProps } from 'animated-number-react';
import { APP_SCORING_PERCENTAGE_RANGES } from '@/constants';
import { TNormalizedRatingCutoffs } from '@/types';

/**
 * Shared default values for ScoreCircle and related hooks.
 */
export const SCORE_CIRCLE_DEFAULTS = {
  circleRatio: 0.75,
  passingThreshold: 80,
  rotation: 0.625,
  simulationType: 'role-play' as const,
  useRolePlayGradient: true,
  value: 0,
  valueMax: 100,
};

/**
 * Configuration for the score animation, used when `useAnimatedNumbers` is true.
 * These values control the delay before the animation starts, the duration of
 * the animation, and the easing function used for the animation.
 */
export const SCORE_CIRCLE_ANIMATION_CONFIG: {
  delay: number;
  duration: number;
  easing: AnimatedNumberReactProps['easing'];
} = {
  delay: 0,
  duration: 700,
  easing: 'easeOutCubic',
};

/**
 * In role-play mode, these are the separator indices that get visible skill band
 * colors. They correspond to the upper bound of L1, upper bound of L2, and lower
 * bound of L4, derived from the provided cutoffs or from the static
 * APP_SCORING_PERCENTAGE_RANGES fallback.
 *
 * In percentage scoring environments, these indices align with the percentage
 * thresholds that define skill levels.
 */
export function getRoleplayVisibleSeparatorIndices(
  runtimeRatingCutoffs?: TNormalizedRatingCutoffs,
): Set<number> {
  const ranges = runtimeRatingCutoffs ?? APP_SCORING_PERCENTAGE_RANGES;
  return new Set([
    ranges.L1[1],
    ranges.L2[1],
    ranges.L4[0],
  ]);
}

/**
 * Static fallback separator indices derived from APP_SCORING_PERCENTAGE_RANGES.
 * Use `getRoleplayVisibleSeparatorIndices()` when runtime cutoffs may be present.
 */
export const ROLEPLAY_VISIBLE_SEPARATOR_INDICES = getRoleplayVisibleSeparatorIndices();

/**
 * Consolidated configuration object for ScoreCircle, combining default values,
 * animation settings, and role-play specific settings. This object can be used
 * to easily access all relevant configuration values for the ScoreCircle
 * component in one place.
 */
export const SCORE_CIRCLE_CONFIG = {
  DEFAULTS: SCORE_CIRCLE_DEFAULTS,
  ANIMATIONS: SCORE_CIRCLE_ANIMATION_CONFIG,
  THRESHOLDS: ROLEPLAY_VISIBLE_SEPARATOR_INDICES,
};
